import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  RedisCacheResponseData,
  UseMutationsParams,
} from '@/shared/interfaces';
import { handleAxiosError } from '@/shared/utils';
import { useUiStore } from '@/store/ui';
import { PlanificadoresKeyActionsEnum } from '../mantenimiento-operaciones';

const { get, post } = erpApi();

///* tanStack query -----------------------------
export const useGetCacheRedis = ({ key }: GetCacheRedisParams) => {
  return useQuery({
    queryKey: ['cache-redis', key],
    queryFn: () => getCacheRedis({ key }),
  });
};

export const useSetCacheRedis = <T>({
  navigate,
  returnUrl,
  returnErrorUrl,
  enableRedirect = true,

  customSuccessMessage,
  enableAlert = true,
  customSuccessCallback,
  customMessageSuccessSeverityAlert = AlertSeverityEnum.success,

  enableErrorRedirect = false,
  customErrorCallback,
  customMessageErrorAlert,
  customMessageErrorSeverityAlert,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: SetCacheRedisValue<T>) => setCacheRedis(params),
    onSuccess: res => {
      queryClient.invalidateQueries({
        queryKey: [PlanificadoresKeyActionsEnum.PLANIFICADORES],
      });

      enableAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Cache guardado correctamente',
          customMessageSuccessSeverityAlert
        );

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      customSuccessCallback && customSuccessCallback(res);
    },
    onError: err => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      customErrorCallback && customErrorCallback(err);

      handleAxiosError(
        err,
        customMessageErrorAlert,
        customMessageErrorSeverityAlert
      );
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};
///* axios -----------------------------
export type GetCacheRedisParams = {
  key: string;
};

export type SetCacheRedisValue<T> = {
  key: string;
  value: T;
};

export const getCacheRedis = async <T>({
  key,
}: GetCacheRedisParams): Promise<RedisCacheResponseData<T> | null> => {
  try {
    return (await get<RedisCacheResponseData<T>>(
      `${urlBackend}api/v1/get-cache/?key=${key}`,
      true
    )) as any;
  } catch (error) {
    handleAxiosError(error);
    return null;
  }
};

export const setCacheRedis = async <T>({
  key,
  value,
}: SetCacheRedisValue<T>) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<RedisCacheResponseData<T>>(
    `${urlBackend}api/v1/set-cache/`,
    { value, key }, // it can overwrite the value if the key already exists
    true
  ) as any;
};
